import {Audio} from '@remotion/media';
import React from 'react';
import {
	AbsoluteFill,
	Easing,
	Img,
	interpolate,
	staticFile,
	useCurrentFrame,
} from 'remotion';

const SANS = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif';
const SERIF = '"Songti SC", "STSong", "Noto Serif SC", "SimSun", serif';
const MONO = '"SF Mono", "Menlo", "Courier New", monospace';
const RED = '#c03028';
const CLAMP = {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'} as const;

type Stroke = {
	id: string;
	name: string;
	path: string;
	cx: number;
	cy: number;
	ex: number;
	ey: number;
	rot: number;
	phase: number;
	drawStart: number;
};

const STROKES: Stroke[] = [
	{id: 'S1', name: '竖', path: 'M 500 148 L 500 252', cx: 500, cy: 200, ex: -360, ey: -250, rot: -34, phase: 0.4, drawStart: 8},
	{id: 'S2', name: '点', path: 'M 372 168 L 424 244', cx: 398, cy: 206, ex: -430, ey: 170, rot: 44, phase: 1.7, drawStart: 19},
	{id: 'S3', name: '撇', path: 'M 632 166 L 566 254', cx: 599, cy: 210, ex: 390, ey: -230, rot: 30, phase: 2.9, drawStart: 30},
	{id: 'S4', name: '横', path: 'M 236 332 L 764 332', cx: 500, cy: 332, ex: 70, ey: 330, rot: -12, phase: 3.8, drawStart: 41},
	{id: 'S5', name: '撇', path: 'M 484 332 L 306 762', cx: 395, cy: 547, ex: -310, ey: 320, rot: -26, phase: 4.6, drawStart: 52},
	{
		id: 'S6',
		name: '竖弯钩',
		path: 'M 548 332 L 548 636 Q 548 702 616 702 L 716 702 Q 776 702 776 646 L 776 612',
		cx: 662,
		cy: 600,
		ex: 420,
		ey: 280,
		rot: 34,
		phase: 5.5,
		drawStart: 63,
	},
];

const Grain: React.FC<{opacity?: number}> = ({opacity = 0.07}) => {
	const frame = useCurrentFrame();
	return (
		<div
			style={{
				position: 'absolute',
				inset: -60,
				backgroundImage: `url(${staticFile('s3/grain.png')})`,
				backgroundRepeat: 'repeat',
				backgroundPosition: `${(frame * 31) % 320}px ${(frame * 47) % 320}px`,
				opacity,
				mixBlendMode: 'overlay',
				pointerEvents: 'none',
			}}
		/>
	);
};

const SPLATTER = [
	{x: 300, y: 742, r: 9},
	{x: 792, y: 668, r: 7},
	{x: 268, y: 344, r: 6},
	{x: 742, y: 246, r: 5},
	{x: 546, y: 780, r: 8},
	{x: 480, y: 130, r: 5},
	{x: 820, y: 420, r: 4},
];

export const LightAnatomy: React.FC = () => {
	const frame = useCurrentFrame();

	const explode = interpolate(frame, [110, 154], [0, 1], {...CLAMP, easing: Easing.out(Easing.cubic)});
	const reassemble = interpolate(frame, [258, 324, 331], [1, -0.06, 0], {...CLAMP, easing: Easing.inOut(Easing.cubic)});
	const factor = frame < 258 ? explode : reassemble;
	const drifting = frame >= 150 && frame < 258;
	const paperP = interpolate(frame, [318, 344], [0, 1], {...CLAMP, easing: Easing.inOut(Easing.quad)});
	const beat = frame >= 108 && frame < 334 ? Math.max(0, Math.sin((Math.PI * 2 * (frame - 108)) / 18)) : 0;
	const zoomBase = frame < 110 ? 1.02 : frame < 258 ? 1 : 1.04;
	const finalZoom = frame >= 345 ? interpolate(frame, [345, 450], [0, 0.022], CLAMP) : 0;
	const scale = (zoomBase + finalZoom) * (1 + beat * 0.012);
	const ink = Math.round(242 + (23 - 242) * paperP);
	const inkG = Math.round(242 + (19 - 242) * paperP);
	const inkB = Math.round(239 + (15 - 239) * paperP);
	const strokeColor = `rgb(${ink}, ${inkG}, ${inkB})`;

	const hudLeft =
		frame < 110
			? '汉字解剖 · ANATOMY OF A CHARACTER'
			: frame < 330
				? 'PHASE 02 · DECONSTRUCTION'
				: 'PHASE 03 · REASSEMBLY';
	const hudRight =
		frame < 110 ? 'SAMPLE 光 / GUĀNG · 6 STROKES' : frame < 330 ? 'DRIFT · ANNOTATED' : 'INK · PAPER · SEAL';

	const scanY = interpolate(frame, [76, 108], [110, 950], CLAMP);
	const scanOn = frame >= 76 && frame < 112;

	const sealP = interpolate(frame, [358, 368], [0, 1], {...CLAMP, easing: Easing.out(Easing.cubic)});
	const captionP = interpolate(frame, [370, 384], [0, 1], {...CLAMP, easing: Easing.out(Easing.quad)});

	const renderStrokes = (bleed: boolean) => (
		<svg
			viewBox="0 0 1000 1000"
			style={{
				position: 'absolute',
				left: 960 - 500 * scale,
				top: 540 - 500 * scale,
				width: 1000 * scale,
				height: 1000 * scale,
				overflow: 'visible',
				filter: bleed ? 'blur(5px)' : 'none',
				opacity: bleed ? 0.32 * paperP : 1,
			}}
		>
			{STROKES.map((stroke) => {
				const draw = interpolate(frame, [stroke.drawStart, stroke.drawStart + 13], [0, 1], {
					...CLAMP,
					easing: Easing.out(Easing.quad),
				});
				const wiggleX = drifting ? Math.sin(frame / 37 + stroke.phase) * 8 : 0;
				const wiggleRot = drifting ? Math.sin(frame / 29 + stroke.phase) * 3.5 : 0;
				const dx = stroke.ex * factor * 0.82 + wiggleX;
				const dy = stroke.ey * factor * 0.82;
				const rot = stroke.rot * factor + wiggleRot;
				return (
					<g
						key={stroke.id}
						style={{
							transformBox: 'fill-box',
							transformOrigin: 'center',
							transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)`,
						}}
					>
						<path
							d={stroke.path}
							pathLength={1}
							stroke={strokeColor}
							strokeWidth={56}
							strokeLinecap="round"
							fill="none"
							strokeDasharray={1}
							strokeDashoffset={1 - draw}
						/>
					</g>
				);
			})}
		</svg>
	);

	return (
		<AbsoluteFill style={{backgroundColor: '#0a0a0c'}}>
			<Audio src={staticFile('anatomy.wav')} />

			<div
				style={{
					position: 'absolute',
					inset: 0,
					backgroundColor: '#efe7d6',
					opacity: paperP,
				}}
			/>
			<div style={{position: 'absolute', inset: 0, opacity: paperP}}>
				<Img
					src={staticFile('s3/paper.jpg')}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						filter: 'brightness(1.5) saturate(0.85)',
					}}
				/>
				<div style={{position: 'absolute', inset: 0, backgroundColor: 'rgba(239,231,214,0.4)'}} />
			</div>

			<div
				style={{
					position: 'absolute',
					inset: 0,
					opacity: interpolate(frame, [0, 10, 300, 330], [0, 0.55, 0.55, 0], CLAMP),
					backgroundImage:
						'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
					backgroundSize: '64px 64px',
				}}
			/>

			<svg
				viewBox="0 0 1000 1000"
				style={{
					position: 'absolute',
					left: 960 - 500 * scale,
					top: 540 - 500 * scale,
					width: 1000 * scale,
					height: 1000 * scale,
					overflow: 'visible',
					opacity: interpolate(frame, [116, 140, 312, 330], [0, 0.55, 0.55, 0], CLAMP),
				}}
			>
				{STROKES.map((stroke) => (
					<path
						key={stroke.id}
						d={stroke.path}
						stroke="rgba(242,242,239,0.16)"
						strokeWidth={4}
						strokeDasharray="14 18"
						strokeLinecap="round"
						fill="none"
					/>
				))}
			</svg>
			{renderStrokes(true)}
			{renderStrokes(false)}

			{drifting
				? STROKES.map((stroke) => {
						const sx = 960 + (stroke.cx - 500) * scale + stroke.ex * factor * 0.82 * scale;
						const sy = 540 + (stroke.cy - 500) * scale + stroke.ey * factor * 0.82 * scale;
						const labelP = interpolate(frame, [150, 165, 246, 258], [0, 1, 1, 0], CLAMP);
						return (
							<div key={stroke.id} style={{position: 'absolute', left: sx, top: sy, opacity: labelP}}>
								<div
									style={{
										position: 'absolute',
										left: -96,
										top: -96,
										width: 192,
										height: 192,
										border: '1px dashed rgba(242,242,239,0.35)',
										borderRadius: '50%',
									}}
								/>
								<div
									style={{
										position: 'absolute',
										left: -96,
										top: -118,
										width: 192,
										textAlign: 'center',
										fontFamily: MONO,
										fontSize: 15,
										letterSpacing: 2,
										color: 'rgba(242,242,239,0.7)',
										whiteSpace: 'nowrap',
									}}
								>
									{stroke.id} · {stroke.name}
								</div>
							</div>
						);
					})
				: null}

			{frame < 120
				? STROKES.map((stroke) => {
						const labelP = interpolate(frame, [stroke.drawStart + 4, stroke.drawStart + 12, stroke.drawStart + 34, stroke.drawStart + 46], [0, 1, 1, 0], CLAMP);
						const sx = 960 + (stroke.cx - 500) * scale;
						const sy = 540 + (stroke.cy - 500) * scale;
						return (
							<div
								key={stroke.id}
								style={{
									position: 'absolute',
									left: sx + 110,
									top: sy - 20,
									opacity: labelP * 0.9,
									fontFamily: MONO,
									fontSize: 16,
									letterSpacing: 2,
									color: RED,
									whiteSpace: 'nowrap',
								}}
							>
								{stroke.id} / {stroke.name}
							</div>
						);
					})
				: null}

			{scanOn ? (
				<div style={{position: 'absolute', left: 200, right: 200, top: scanY}}>
					<div style={{height: 2, backgroundColor: RED, boxShadow: `0 0 22px ${RED}`}} />
					<div
						style={{
							marginTop: 10,
							fontFamily: MONO,
							fontSize: 14,
							letterSpacing: 3,
							color: 'rgba(192,48,40,0.85)',
							textAlign: 'right',
						}}
					>
						SCANNING {Math.round(interpolate(frame, [76, 108], [0, 100], CLAMP))}%
					</div>
				</div>
			) : null}

			{paperP > 0.2
				? SPLATTER.map((dot, index) => (
						<div
							key={index}
							style={{
								position: 'absolute',
								left: dot.x,
								top: dot.y,
								width: dot.r * 2,
								height: dot.r * 2,
								borderRadius: '50%',
								backgroundColor: 'rgba(23,19,15,0.75)',
								filter: 'blur(1.5px)',
								opacity: paperP * interpolate(frame, [330 + index * 3, 344 + index * 3], [0, 1], CLAMP),
							}}
						/>
					))
				: null}

			<div
				style={{
					position: 'absolute',
					left: 1494,
					top: 620,
					width: 132,
					height: 132,
					backgroundColor: RED,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: '#f3ead9',
					fontFamily: SERIF,
					fontSize: 46,
					fontWeight: 700,
					letterSpacing: 2,
					lineHeight: 1.1,
					textAlign: 'center',
					transform: `rotate(-7deg) scale(${1 + (1 - sealP) * 0.7})`,
					opacity: sealP,
					boxShadow: '0 14px 40px rgba(90,20,14,0.35)',
				}}
			>
				字
				<br />
				灵
			</div>

			<div
				style={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 118,
					textAlign: 'center',
					fontFamily: SERIF,
					fontSize: 46,
					fontWeight: 700,
					letterSpacing: 10,
					color: 'rgba(35,29,22,0.92)',
					opacity: captionP,
					transform: `translateY(${(1 - captionP) * 16}px)`,
				}}
			>
				拆开是六笔，合起来是光
			</div>

			<div
				style={{
					position: 'absolute',
					left: 90,
					top: 76,
					fontFamily: MONO,
					fontSize: 15,
					letterSpacing: 4,
					color: paperP > 0.5 ? 'rgba(42,35,27,0.5)' : 'rgba(242,242,239,0.5)',
				}}
			>
				{hudLeft}
			</div>
			<div
				style={{
					position: 'absolute',
					right: 90,
					top: 76,
					fontFamily: MONO,
					fontSize: 15,
					letterSpacing: 4,
					color: paperP > 0.5 ? 'rgba(42,35,27,0.5)' : 'rgba(242,242,239,0.5)',
				}}
			>
				{hudRight}
			</div>
			<div
				style={{
					position: 'absolute',
					right: 90,
					bottom: 56,
					fontFamily: MONO,
					fontSize: 12,
					letterSpacing: 3,
					color: 'rgba(42,35,27,0.35)',
					opacity: paperP,
				}}
			>
				ANATOMY OF A CHARACTER · REMOTION
			</div>

			<Grain opacity={0.06 + paperP * 0.06} />
		</AbsoluteFill>
	);
};
